import { useState } from 'react'
import { Plus, Send } from 'lucide-react'
import {
  EmptyState, ErrorState, FileUploader, FormDialog, PageHeader, StatusBadge, type UploadedFile,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useCurrentUser } from '@/hooks/usePermission'
import { toApiProblem } from '@/lib/api/client'
import { RequestFormFields } from '../components/RequestFormFields'
import { useMyRequests, useRequestForm, useRequestMutations, useRequestTypes } from '../api'
import { REQUEST_STATUS_TONE } from '../types'
import { Select } from '@/components/ui/select'

/**
 * Employee self-service: raise a request of whatever kinds this installation has defined.
 *
 * <p>Raising and submitting are one button. A draft nobody submits is indistinguishable from
 * a request that was never raised, and the approver is the point — the same call the transfer
 * wizard makes for the same reason.
 */
export function MyRequestsPage() {
  useBreadcrumb()
  const user = useCurrentUser()
  const requests = useMyRequests()
  const types = useRequestTypes(true)
  const { raise, submit } = useRequestMutations()

  const [open, setOpen] = useState(false)
  const [typeId, setTypeId] = useState('')
  const [subject, setSubject] = useState('')
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [fileId, setFileId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const form = useRequestForm(typeId || undefined)
  const selectedType = types.data?.find((type) => type.id === typeId)

  function reset() {
    setTypeId('')
    setSubject('')
    setValues({})
    setFileId(null)
    setError(null)
  }

  async function submitRequest() {
    setError(null)
    if (!user?.employeeId) {
      setError('Your account is not linked to an employee record, so it cannot raise requests.')
      return
    }
    try {
      const created = await raise.mutateAsync({
        requestTypeId: typeId,
        employeeId: user.employeeId,
        subject: subject || null,
        payload: values,
        attachmentFileId: fileId,
      })
      await submit.mutateAsync(created.id)
      setOpen(false)
      reset()
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That request could not be raised')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="My requests"
        subtitle="Anything this company accepts a request for — the list is whatever HR has defined."
        actions={
          <Button type="button" onClick={() => { reset(); setOpen(true) }}>
            <Plus className="size-4" aria-hidden="true" />
            New request
          </Button>
        }
      />

      <Card>
        {requests.isPending && <Skeleton className="m-4 h-24" />}
        {requests.isError && (
          <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />
        )}
        {requests.isSuccess && requests.data.length === 0 && (
          <EmptyState
            title="You have not raised anything"
            description="New request starts one; the form is whatever the type asks for."
          />
        )}
        {(requests.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[40rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">Reference</th>
                  <th scope="col" className="p-3 font-medium">Kind</th>
                  <th scope="col" className="p-3 font-medium">Subject</th>
                  <th scope="col" className="p-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {requests.data?.map((request) => (
                  <tr key={request.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-3"><code className="text-xs">{request.requestNo}</code></td>
                    <td className="p-3 text-[var(--color-heading)]">{request.requestTypeName}</td>
                    <td className="p-3 text-[var(--color-muted)]">{request.subject}</td>
                    <td className="p-3">
                      <StatusBadge status={request.status} tone={REQUEST_STATUS_TONE[request.status]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={open}
        onOpenChange={(next) => { if (!next) { setOpen(false); reset() } }}
        title="New request"
        description="Pick what you need; the form changes to whatever that kind of request asks for."
        submitLabel="Raise and send"
        loading={raise.isPending || submit.isPending}
        error={error}
        onSubmit={() => void submitRequest()}
        size="lg"
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="request-type">What do you need?</Label>
            <Select
              id="request-type"
              required
              value={typeId}
              onChange={(event) => { setTypeId(event.target.value); setValues({}) }}
              className="w-full"
            >
              <option value="">Choose a request type…</option>
              {(types.data ?? []).map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </Select>
            {selectedType?.slaHours != null && (
              <p className="text-xs text-[var(--color-muted)]">
                Usually answered within {selectedType.slaHours} hours.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="request-subject">Subject</Label>
            <Input
              id="request-subject"
              value={subject}
              placeholder={selectedType?.name ?? ''}
              onChange={(event) => setSubject(event.target.value)}
            />
          </div>

          {typeId && (
            <RequestFormFields
              fields={form.data ?? []}
              values={values}
              onChange={setValues}
              disabled={form.isPending}
            />
          )}

          {selectedType?.requiresAttachment && (
            <div className="space-y-1.5">
              <Label>Attachment</Label>
              <FileUploader
                ownerType="generic_request"
                onUploaded={(file: UploadedFile) => setFileId(file.id)}
                onRemoved={() => setFileId(null)}
              />
              <p className="text-xs text-[var(--color-muted)]">
                This request type needs a document attached.
              </p>
            </div>
          )}

          <p className="flex items-center gap-2 text-xs text-[var(--color-muted)]">
            <Send className="size-3.5" aria-hidden="true" />
            Raising it sends it straight for approval.
          </p>
        </div>
      </FormDialog>
    </div>
  )
}
